// src/components/login/LoginPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User } from 'lucide-react';
import { auth } from '../../firebase';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';

const LoginPage = ({ isMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(true);
  const [error, setError] = useState('');
  const [redirectDestination, setRedirectDestination] = useState(null);
  
  // Check URL path to determine if we're on the login or register page
  useEffect(() => {
    const path = location.pathname;
    if (path === '/register') {
      setIsLogin(false);
    } else {
      setIsLogin(true);
    }
  }, [location.pathname]);
  
  // Check for redirect parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const redirect = searchParams.get('redirect');
    if (redirect) {
      setRedirectDestination(redirect);
    }
    
    // Check if there's a pending booking
    const pendingBooking = localStorage.getItem('pendingBooking');
    if (pendingBooking) {
      try {
        const bookingData = JSON.parse(pendingBooking);
        if (bookingData && bookingData.returnPath) {
          setRedirectDestination(bookingData.returnPath.replace('/', ''));
        }
      } catch (e) {
        console.error('Error parsing pending booking:', e);
      }
    }
  }, [location]);
  
  // Check if user is already logged in
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (user) {
        // User is already signed in, redirect appropriately
        if (redirectDestination === 'booking') {
          navigate('/book');
        } else {
          navigate('/account');
        }
      }
    });
    
    return () => unsubscribe();
  }, [navigate, redirectDestination]);
  
  const containerClass = isMobile 
    ? "px-4 py-6 mt-20" 
    : "container mx-auto px-6 py-12 mt-24";
  
  // When user switches from login to signup or vice versa, update the URL
  const handleSwitchMode = (mode) => {
    if (mode === 'signup') {
      setIsLogin(false);
      // Update URL without causing a page reload
      navigate('/register' + location.search, { replace: true });
    } else {
      setIsLogin(true);
      navigate('/login' + location.search, { replace: true });
    }
    setError('');
  };
  
  return (
    <div className={`min-h-screen bg-gray-50 flex flex-col ${containerClass}`}>
      <div className="max-w-md mx-auto w-full bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-6 bg-gradient-to-r from-[#3A98B9] to-[#5FBDDD] text-white">
          <div className="flex items-center justify-center">
            <div className="h-16 w-16 rounded-full bg-white/20 flex items-center justify-center">
              <User className="h-8 w-8" />
            </div>
          </div>
          <h2 className="mt-4 text-center text-2xl font-bold">
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </h2>
          <p className="mt-1 text-center text-white/80">
            {isLogin ? 'Sign in to access your account' : 'Join us and start booking services'}
          </p>
          
          {redirectDestination === 'booking' && (
            <div className="mt-3 text-center text-sm bg-white/20 py-2 px-3 rounded-md">
              {isLogin ? 'Sign in' : 'Create an account'} to continue with your booking
            </div>
          )}
        </div>
        
        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg">
              {error}
            </div>
          )}
          
          {isLogin ? (
            <LoginForm 
              onSwitchMode={() => handleSwitchMode('signup')}
              setError={setError}
            />
          ) : (
            <SignupForm 
              onSwitchMode={() => handleSwitchMode('login')}
              setError={setError}
              redirectDestination={redirectDestination}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;